import {getRandomColor} from "../../../utils/GetRandomColor";
import {MathJax, MathJaxContext} from "better-react-mathjax";

import onelayernn from "../../../assets/img/blog/mathematicalArtificialIntelligence/activationFunctions/1layernn.png";
import sigmoidImg from "../../../assets/img/blog/mathematicalArtificialIntelligence/activationFunctions/sigmoid.png";
import tanhImg from "../../../assets/img/blog/mathematicalArtificialIntelligence/activationFunctions/tanh.png";
import reluImg from "../../../assets/img/blog/mathematicalArtificialIntelligence/activationFunctions/relu.png";

import fourierImg from "../../../assets/img/blog/mathematicalArtificialIntelligence/fourier.png"
import gdImg from "../../../assets/img/blog/mathematicalArtificialIntelligence/gradientDescent/gd.png"
import momentumImg from "../../../assets/img/blog/mathematicalArtificialIntelligence/gradientDescent/momentum.png"


export default function Calculus() {
    const imgStyle = {
        backgroundColor: "#E9E8E6",
        border: `5px solid ${getRandomColor()}`,
    }

    const altTextStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        display: "block",
        textAlign: "center",
        marginTop: "-10px",
        marginBottom: "20px",
        fontStyle: "italic",
    }

    const mathJaxConfig = {
        loader: {load: ["[tex]/html"]},
        tex: {
            packages: {"[+]": ["html"]},
            inlineMath: [["$", "$"], ["\\(", "\\)"]],
            displayMath: [["$$", "$$"], ["\\[", "\\]"]]
        }
    }

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className={"blog-body"}>
                <h2>Calculus</h2>
                <p>
                    Infinitesimal calculus, which has its roots in the researches of Newton and Leibniz (Rosenthal,
                    1951), has not lost its importance since the day of its discovery. Machine learning algorithms
                    aim
                    to optimise their objective functions as the parameters change, and the role
                    of the calculus is to represent the performance of the algorithm (Brownlee, Cristina, &
                    Saeed, 2022). In addition to the optimization methods and the objective functions, another
                    application of this topic is to ensure the nonlinear behaviour in regression problems
                    using the activation functions. Also, this section explores how Fourier series are used in
                    time series analysis
                </p>

                <h3>Objective Functions</h3>
                <p>
                    The goal of machine learning algorithms is to optimize their objective functions. Depending on
                    the
                    model, it may be beneficial to increase or decrease the value of the objective
                    functions. Supervised learning models aim to minimize the loss function, while the goal
                    of reinforced learning models is to maximize the reward function.
                </p>

                <h4>L2-norm Loss</h4>
                <p>
                    The L2 norm loss or squared loss is a widely used loss function in regression problems. It can
                    be
                    calculated like the mean squared error.
                </p>

                <MathJax>{'\\begin{align}\n\\sum_{i}{}(y_i - \\hat{y}_i)^2\n\\end{align}'}</MathJax>

                <p>
                    It is basically the Euclidean distance between the observations and the predictions, hence the
                    name
                    norm.
                </p>

                <h4>Cross-entropy</h4>
                <p>
                    Cross entropy is a loss function used in classification problems. The classification metrics
                    mentioned in the previous section are discrete, since these metrics are nothing more than a
                    ratio of
                    counts. Cross-entropy, on the other hand, is continuous, and this continuity is required for
                    gradient descent algorithms.
                </p>

                <p>The formula of the cross-entropy is</p>

                <MathJax>{'\\begin{align}\nL(\\hat{y}_{i},y_{i})=-\\sum_{i}{} y_{i} \\ln\\hat{y}_{i}\n\\end{align}'}</MathJax>

                <p>
                    where <MathJax inline>{'$y_{i}=\\text{target value}$'}</MathJax> and <MathJax
                    inline>{'$\\hat{y}_{i}=\\text{predicted value}$'}</MathJax>. The value of
                    cross-entropy decreases with better prediction. For example, consider a visual data set
                    consisting
                    of dog, cat and bird images, where the target vectors are defined as follows.
                </p>

                <pre><code className="language-python">dog = [1, 0, 0]
cat = [0, 1, 0]
bird = [0, 0, 1]</code></pre>

                <p>
                    If the model makes a prediction based on a picture of a cat and calculates that the animal in the
                    picture is 20% dog, 40% cat, and 40% bird
                </p>

                <pre><code className="language-python">prediction = [0.2, 0.4, 0.4]</code></pre>

                <p>the cross-entropy would be</p>

                <MathJax>{'\\begin{align}\nL(\\hat{y}_{i},y_{i})=-0\\cdot \\ln0.2-1\\cdot \\ln0.4-0\\cdot \\ln0.4=0.91\n\\end{align}'}</MathJax>

                <p>
                    Loss functions are used to reduce variance by training the model with the training data. Metrics, on
                    the other hand, are used to reduce bias calculated using the test data.
                </p>

                <h3>Activation Functions</h3>
                <p>
                    As the relationships between variables become more complex, linear approximations are
                    no longer sufficient. In neural networks, activation functions are used to convert linear
                    combinations into nonlinear ones. This process is necessary to stack the layers, because a
                    network consisting only of linear combinations is nothing more than a linear regression.
                    For a better explanation, see Figure 4.
                </p>

                <img style={imgStyle} className={"blog-img"} src={onelayernn} alt="One layer neural network"/>
                <div style={altTextStyle}><p><b>Figure 4:</b> A rather small neural network with inputs and weights.</p>
                </div>

                <p>
                    In this model, <MathJax inline>{'$y$'}</MathJax> is
                </p>

                <MathJax>{'\\begin{align}\ny=x_1 w_1 + x_2 w_2 + x_3 w_2 + b w_4\n\\end{align}'}</MathJax>

                <p>
                    It can be stated that neural networks consisting only of input and output layers are linear
                    regression. For further reading, below are some of the most commonly used activation functions.
                </p>

                <h4>Sigmoid</h4>
                <p>
                    The sigmoid function, also known as the logistic function, is an activation function used in
                    neural
                    networks. Since the sigmoid function ranges between <MathJax inline>{'$(0,1)$'}</MathJax>, it is
                    generally used when the
                    desired
                    prediction is to be a probability. Its formula is as follows.
                </p>

                <MathJax>{'\\begin{align}\n\\sigma(a)=\\frac{1}{1 + e ^ { - a}}\n\\end{align}'}</MathJax>

                <p>
                    Derivatives of the activation functions are used in backpropagation. The derivative of the
                    sigmoid
                    function is
                </p>

                <MathJax>{'\\begin{align}\n\\frac{\\partial \\sigma(a)}{\\partial a}=\\sigma(a)(1-\\sigma(a))\n\\end{align}'}</MathJax>

                <p>Python code that plots the sigmoid function is given below. See Figure 5.</p>

                <pre><code className="language-python">a = np.arange(-5, 5, 0.01)  # defining a range to plot the function on
sigmoid = 1 / (1 + np.exp(-a))  # the sigmoid function
plt.axvline(x=0, color="red")
plt.axhline(y=0, color="red")  # x and y axis
plt.plot(a, sigmoid, lw=3) # plotting the sigmoid function</code></pre>

                <img style={imgStyle} className={"blog-img"} src={sigmoidImg} alt="Sigmoid function"/>
                <div style={altTextStyle}><p><b>Figure 5:</b> The sigmoid function.</p></div>

                <h4>tanh</h4>
                <p>
                    The hyperbolic tangent function is another activation function used in neural networks. The
                    output
                    of the <MathJax inline>{'$\\tanh$'}</MathJax> function is between <MathJax
                    inline>{'$(-1,1)$'}</MathJax>. Its formula is
                </p>

                <MathJax>{'\\begin{align}\n\\tanh(a)=\\frac{e ^ a - e ^ { - a}}{e ^ a + e ^ { - a}}\n\\end{align}'}</MathJax>

                <p>And the derivative of the <MathJax inline>{'$\\tanh$'}</MathJax> function is</p>

                <MathJax>{'\\begin{align}\n\\frac{\\partial \\tanh(a)}{\\partial a}=\\frac{4}{(e ^ a + e ^ { - a})^2}\n\\end{align}'}</MathJax>

                <p>See Figure 6 for this function's graph.</p>

                <pre><code className="language-python">a = np.arange(-5, 5, 0.01)  # defining a range to plot the function on
tanh = (np.exp(a) - np.exp(-a)) / (np.exp(a) + np.exp(-a))  # the tanh function
plt.axvline(x=0, color="red")
plt.axhline(y=0, color="red")  # x and y axis
plt.plot(a, tanh, lw=3)  # plotting the tanh function
</code></pre>

                <img style={imgStyle} className={"blog-img"} src={tanhImg} alt="tanh function"/>
                <div style={altTextStyle}><p><b>Figure 6:</b> The tanh function.</p></div>

                <h4>ReLU</h4>
                <p>
                    The rectified linear unit function, <i>ReLU</i> for short, is an activation function used when
                    all
                    values below <MathJax inline>{'$0$'}</MathJax> are considered unimportant. It ranges
                    between <MathJax inline>{'$(0,\\infty)$'}</MathJax> and sets all values
                    below
                    <MathJax inline>{'$0$'}</MathJax> to <MathJax inline>{'$0$'}</MathJax>. The formula of the ReLU
                    function is
                </p>

                <MathJax>{'\\begin{align}\n\\operatorname{relu}(a)=\\max(0,a)\n\\end{align}'}</MathJax>

                <p>And its derivative is</p>

                <MathJax>{'\\begin{align}\n\\frac{\\partial \\operatorname{relu}(a)}{\\partial a}=\\left\\{\\begin{array}{l}\n0, \\text {if } a \\leq 0 \\\\\n1, \\text {if } a>0\n\\end{array}\\right.\n\\end{align}'}</MathJax>

                <p>Python code given below plots the ReLU function and its graph can be seen in Figure 7.</p>

                <pre><code className="language-python">a = np.arange(-5, 5, 0.01)  # defining a range to plot the function on
relu = np.maximum(0, a)  # the relu function
plt.axvline(x=0, color="red")
plt.axhline(y=0, color="red")  # x and y axis
plt.plot(a, relu, lw=3)  # plotting the relu function
</code></pre>

                <img style={imgStyle} className={"blog-img"} src={reluImg} alt="ReLU function"/>
                <div style={altTextStyle}><p><b>Figure 7:</b> The ReLU function.</p></div>

                <h4>softmax</h4>
                <p>
                    The softmax activation function is used to transform imported values into an accurate
                    probability
                    distribution. Thus, the sum of the outputs of the softmax activation function is equal to <MathJax
                    inline>{'$1$'}</MathJax>.
                    The
                    formula is</p><p>

                <MathJax>{'\\begin{align}\n\\sigma_{\\mathrm{i}}(a)=\\frac{e ^ {a_{i}}}{\\sum_{j} e^{a_{j}}}\n\\end{align}'}</MathJax>
            </p><p>
                And the derivative of the softmax function is
            </p><p>
                <MathJax>{'\\begin{align}\n\\frac{\\partial \\sigma_{i}(a)}{\\partial a_{j}}=\\sigma_{i}(a)\\left(\\delta_{i\nj}-\\sigma_{j}(a)\\right)\n\\end{align}'}</MathJax>
            </p><p>
                where <MathJax inline>{'$\\delta_{i j}$'}</MathJax> is the Kronecker delta, i.e., <MathJax
                inline>{'$i=j$'}</MathJax>, <MathJax inline>{'$\\delta_{i j}=1$'}</MathJax> and if <MathJax
                inline>{'$i\\neq j$'}</MathJax>,
                <MathJax inline>{'$\\delta_{i j}=0$'}</MathJax>.
            </p><p>
                The following Python code defines the softmax function and converts the input values into
                probabilities.
            </p>
                <pre><code className="language-python">softmax = lambda x: np.exp(x) / np.exp(x).sum()  # defining the softmax function
inputValues = [12, 3, 29, 14, 8] # random values
outputValues = softmax(inputValues)
</code></pre>
                <p>
                    The output values are
                    <code>[4.13993628e-08, 5.10908725e-12, 9.99999652e-01, 3.05902214e-07, 7.58255779e-10]</code>.
                    And the sum of the probabilities can be calculated as follows.
                </p>
                <pre><code className="language-python">sum(outputValues)</code></pre>
                <p>
                    The output is <code>1</code>.
                </p>

                <h3>Fourier Series</h3>
                <p>
                    Fourier series are used as features in time series analysis. The goal is to let the model
                    learn from these Fourier features by breaking down the series into sine and cosine waves.
                    All types of time series analysis performed using sinusoids are called Fourier analysis
                    (Bloomfield, 2004)</p><p>
                The Figure 8 below nicely illustrates how the summation of Fourier features estimates
                seasonality (Holbrook, 2021).
            </p>

                <img style={imgStyle} className={"blog-img"} src={fourierImg} alt="fourier series"/>
                <div style={altTextStyle}><p><b>Figure 8:</b> Sine and cosine waves can be used to determine
                    seasonality.</p></div>

                <p>

                    Fourier features can be implemented via statsmodels library. This library includes the
                    CalenderFourier method. The code block below generates 4 sine and cosine pairs for monthly
                    seasonality.
                </p>

                <pre><code className="language-python">from statsmodels.tsa.deterministic import CalendarFourier

fourier = CalendarFourier(freq="M", order=4)</code></pre>

                <h3>Optimization</h3>
                <p>
                    Machine learning is all about optimization. The main goal of machine learning algorithms
                    is to change the values of the weights to achieve the best result of the loss function and
                    metrics. This goal is achieved with the gradient descent algorithm, which has been further
                    developed over the years.
                </p>

                <h4>Gradient Descent</h4>
                <p>
                    Gradient descent is an iterative process used to find minima or maxima of a function. In machine
                    learning, gradient descent is used as an optimization algorithm. The updated value of <MathJax
                    inline>{'$x$'}</MathJax> is
                    calculated with
                </p><p>
                <MathJax>{'\\begin{align}\nx_{i + 1}=x_i - \\eta f\'(x_i)\n\\end{align}'}</MathJax>
            </p><p>
                where <MathJax inline>{'$\\eta$'}</MathJax> is the learning rate. The gradient descent algorithm can
                also be applied to higher
                dimensions. Consider a linear regression model <MathJax
                inline>{'$x_i w + b = \\hat{y}_i \\rightarrow y_i$'}</MathJax> with the
                loss
                function <MathJax inline>{'$L$'}</MathJax>. The updated values for the weight and the bias are
                calculated using the following
                formulas.
            </p><p>
                <MathJax>{'\\begin{align}\nw_{i + 1}&=w_i - \\eta \\nabla_w L(\\hat{y},y) \\\\\nb_{i + 1}&=b_i - \\eta \\nabla_b L(\\hat{y},y)\n\\end{align}'}</MathJax>

                The gradient descent algorithm is demonstrated in Figure 9.
            </p>

                <img style={imgStyle} className={"blog-img"} src={gdImg} alt="gradient descent"/>
                <div style={altTextStyle}><p><b>Figure 9:</b> The gradient descent algorithm.</p></div>

                <h5>Stochastic Gradient Descent</h5>
                <p>
                    One method for speeding up the process of updating
                    weights is batching. Instead of updating the weights after each epoch, the data set is split
                    into batches to update the weights multiple times in a single epoch. With this approach,
                    computers are able to train different batches using CPU cores. This algorithm is called
                    stochastic gradient descent. The term ”stochastic” refers to the randomness of the batches
                    selected.
                </p>

                <h4>Momentum</h4>
                <p>
                    The gradient descent algorithm alone sometimes fails to find a global minimum, but only the
                    local
                    minima (see Figure 10). Stopping the optimization at an extrema point can be prevented by adding
                    momentum to the gradient descent. Recall the formula for gradient descent at moment <MathJax
                    inline>{'$t$'}</MathJax>.
                </p><p>
                <MathJax>{'\\begin{align} \nw(t+1) = w(t)- \\eta\\frac{\\partial L}{\\partial w}(t)\n\\end{align}'}</MathJax>
            </p><p>
                Putting the descent at time <MathJax inline>{'$(t-1)$'}</MathJax> into the formula for the gradient
                descent and multiplying it
                by
                the constant <MathJax inline>{'$\\gamma$'}</MathJax>, the following formula is acquired.
            </p><p>
                <MathJax>{'\\begin{align}\nw(t+1) = w(t) - \\eta\\frac{\\partial L}{\\partial w}(t) - \\gamma\\eta\\frac{\\partial L}{\\partial\nw}(t-1)\n\\end{align}'}</MathJax>
            </p><p>
                where <MathJax inline>{'$0\\leq \\gamma \\leq 1$'}</MathJax>. Multiplying the previous descent by a
                number between 0 and 1
                reduces
                the effectiveness of the previous steps with each iteration.
            </p>

                <img style={imgStyle} className={"blog-img"} src={momentumImg} alt="momentum"/>
                <div style={altTextStyle}><p><b>Figure 10:</b> The gradient descent might remain insufficient.</p></div>

                <h4>Learning Rate</h4>
                <p>

                    The learning rate is a parameter that determines the speed of convergence of the algorithm to
                    the
                    extrema point. It is important to choose an appropriate learning rate that is not too high and
                    not
                    too low, since a very high learning rate will cause oscillation and a very low learning rate
                    will
                    cause the algorithm to iterate continuously. The learning rate is denoted by <MathJax
                    inline>{'$\\eta$'}</MathJax>.
                </p><p>
                Updating the learning rate across epochs improves algorithm performance. Small learning rates
                may
                not be necessary at the beginning, but to prevent the gradient descent algorithm from
                oscillating,
                the learning rate must eventually be lowered. This process is called a learning rate schedule.
            </p>

                <h5>Piecewise Learning Rate</h5>
                <p>
                    The piecewise learning rate updates the learning rate for a fixed number of epochs. For example,
                    starting from a learning rate <MathJax inline>{'$\\eta=0.1$'}</MathJax> yields the following
                    piecewise learning rate schedule.

                </p>
                <ol>
                    <li><MathJax inline>{'$\\eta=0.1$'}</MathJax> for the first 10 epochs</li>
                    <li><MathJax inline>{'$\\eta=0.05$'}</MathJax> for the following 10 epochs</li>
                    <li><MathJax inline>{'$\\eta=0.025$'}</MathJax> for the following 10 epochs</li>
                    <li><MathJax inline>{'$\\eta=0.001$'}</MathJax> until the last epoch</li>
                </ol>

                <h5>Exponential Learning Rate</h5>
                <p>
                    The exponential learning rate updates the learning rate according to the following formula.
                </p><p>
                <MathJax>{'\\begin{align}\n\\eta=\\eta_0 e^{n / c}\n\\end{align}'}</MathJax>
            </p><p>
                where <MathJax inline>{'$n=\\text{number of epochs}$'}</MathJax> and <MathJax
                inline>{'$c=\\text{a constant}$'}</MathJax>. <MathJax inline>{'$c$'}</MathJax> is a hyperparameter
                proportional to
                the number of epochs.
            </p>

                <h4>Adaptive Learning Rate Schedules</h4>
                <p>
                    The learning rate can also be dynamically updated for individual weights. This method helps to
                    reduce the training time of
                    the model (Okewu, Misra, & Lius, 2020). Some of the adaptive learning rate schedules
                    are listed below.
                </p>

                <h5>Adaptive Gradient Algorithm</h5>
                <p>
                    Adaptive gradient algorithm, or AdaGrad, is an
                    adaptive learning rate schedule that dynamically varies the learning rate as a function of
                    feature frequency (Lydia & Francis, 2019). The formula of the adaptive gradient algorithm
                    can be obtained by manipulating the momentum formula.
                </p><p>
                <MathJax>{'\\begin{align}\nw(t+1)-w(t)&=-\\eta\\frac{\\partial L}{\\partial w}(t) \\\\\n\\Delta w &= -\\eta\\frac{\\partial L}{\\partial w}(t)\n\\end{align}'}</MathJax>
            </p><p>
                Replacing <MathJax inline>{'$w$'}</MathJax> by the weight index <MathJax inline>{'$w_i$'}</MathJax> and
                dividing the learning rate <MathJax inline>{'$\\eta$'}</MathJax> by <MathJax
                inline>{'$\\sqrt{G_i(t)\n+\n\\epsilon}$'}</MathJax>, the formula of the adaptive gradient algorithm is
                obtained.
            </p><p>
                <MathJax>{'\\begin{align} \n\\Delta w_i (t)=-\\frac{\\eta}{\\sqrt{G_i (t)}+\\epsilon}\\frac{\\partial L}{\\partial w_i}(t)\n\\end{align}'}</MathJax>
            </p><p>
                where
            </p><p>
                <MathJax>{'\\begin{align}\nG_i (t) = G_i (t-1) + \\left(\\frac{\\partial L}{\\partial w_i}(t)\\right)^2\n\\end{align}'}</MathJax>
            </p><p>
                The reason for the absence of <MathJax inline>{'$\\epsilon$'}</MathJax> in the denominator is to avoid
                division by zero, since <MathJax inline>{'$G_i\n(0)=0$'}</MathJax>.
            </p>

                <h5>Root Mean Square Propagation</h5>
                <p>
                    Root mean square propagation, or RMSprop in short, is an adaptive learning rate schedule similar
                    to
                    AdaGrad with a different <MathJax inline>{'$G_i (t)$'}</MathJax> function. The formula is the same
                    as the formula of AdaGrad,
                    where
                    <MathJax inline>{'$G_i (t)$'}</MathJax> is
                </p><p>
                <MathJax>{'\\begin{align}\nG_i (t) =\\gamma G_i (t-1) + (1-\\gamma)\\left(\\frac{\\partial L}{\\partial w_i}(t)\\right)^2\n\\end{align}'}</MathJax>
            </p><p>
                Where <MathJax inline>{'$\\gamma$'}</MathJax> is a hyperparameter. Note that <MathJax
                inline>{'$G_i (t)$'}</MathJax> in RMSprop is not monotonically
                decreasing
                like the AdaGrad algorithm.
            </p>

                <h5>Adaptive Moment Estimation</h5>
                <p>
                    Adaptive moment estimation (ADAM) combines momentum with adaptive learning rate schedules. The
                    goal
                    of adaptive moment estimation is to gain control over step sizes to solve problems caused by the
                    gradient descent algorithm alone. The formula for adaptive moment estimation is
                </p><p>
                <MathJax>{'\\begin{align}\n\\Delta w_i (t)=-\\frac{\\eta}{\\sqrt{G_i (t)}+\\epsilon}M_i (t)\n\\end{align}'}</MathJax>
            </p><p>
                where
            </p><p>
                <MathJax>{'\\begin{align}\nM_i (t)=\\gamma M_i (t-1)+(1-\\gamma)\\frac{\\partial L}{\\partial w_i}(t)\n\\end{align}'}</MathJax>
            </p>

                <h4>Initialization</h4>
                <p>
                    Randomly selecting initial values for the weights from the standard normal distribution or the
                    uniform distribution causes models with nonlinear activation
                    functions to perform poorly, especially models
                    with activation functions such as sigmoid and tanh, because the input values around the
                    mean exhibit linear behaviour (Glorot & Bengio, 2010). Xavier initialization or Glorot
                    initialization, named after Xavier Glorot, helps improve the performance of machine learning
                    models
                    by using the number of input and output values to determine the distribution
                    parameters.
                </p>

                <h5>Uniform Xavier Initialization</h5>
                <p>
                    The selection of the weights from a uniform distribution in the range of <MathJax
                    inline>{'$[-x,x]$'}</MathJax>, where <MathJax inline>{'$x$'}</MathJax>
                    stands
                    for
                </p>

                <MathJax>{'\\begin{align}\nx=\\sqrt{\\frac{6}{\\text{number of inputs}+\\text{number of outputs}}}\n\\end{align}'}</MathJax>

                <h5>Normal Xavier Initialization</h5>
                <p>
                    The selection of the weights from a normal distribution where <MathJax
                    inline>{'$\\mu=0$'}</MathJax> and

                </p>
                <MathJax>{'\\begin{align}\n\\sigma=\\sqrt{\\frac{6}{\\text{number of inputs}+\\text{number of outputs}}}\n\\end{align}'}</MathJax>
            </div>
        </MathJaxContext>
    )
}